import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import LegalDocumentEditorModal from '../../components/modals/LegalDocumentEditorModal';
import LegalDocumentPreviewModal from '../../components/modals/LegalDocumentPreviewModal';

const AppMetadataPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    release_notes: '',
    latest_app_version: '',
    force_update: false,
    update_url_ios: '',
    update_url_android: '',
    maintenance_mode: false,
    maintenance_message: '',
    features_enabled: {},
    support_email: '',
    support_website: '',
    company_name: '',
    company_address: '',
    terms_of_service_url: '',
    privacy_policy_url: ''
  });

  // Modal states
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  // Legal document modal states
  const [legalEditorModal, setLegalEditorModal] = useState({ 
    show: false, 
    type: null, // 'terms' or 'privacy'
    content: '',
    saving: false 
  });
  const [legalPreviewModal, setLegalPreviewModal] = useState({ 
    show: false, 
    type: null,
    content: '',
    lastUpdated: null
  });
  const [legalDocuments, setLegalDocuments] = useState({
    terms_content: '',
    terms_last_updated: null,
    privacy_content: '',
    privacy_last_updated: null
  });

  useEffect(() => {
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/super-admin/app/metadata`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = response.data.data;
      setMetadata(data);
      setFormData({
        release_notes: data.release_notes || 'New features:\n- Improved offline sync performance\n- Enhanced photo compression\n- Bug fixes and stability improvements\n- New task assignment workflow\n- Better error handling',
        latest_app_version: data.latest_app_version || '1.0.0',
        force_update: data.force_update || false,
        update_url_ios: data.update_url_ios || 'https://apps.apple.com/app/siteorbit/id123456789',
        update_url_android: data.update_url_android || 'https://play.google.com/store/apps/details?id=com.siteorbit.app',
        maintenance_mode: data.maintenance_mode || false,
        maintenance_message: data.maintenance_message || 'The app is currently undergoing scheduled maintenance. We expect to be back online shortly. Thank you for your patience.',
        features_enabled: data.features_enabled || {
          offline_clock_in: true,
          photo_upload: true,
          task_management: true,
          expenses: true,
          overtime: true,
          materials: true,
          holidays: true
        },
        support_email: data.support_email || 'support@webmail.siteorbit.app',
        support_website: data.support_website || 'https://siteorbit.app',
        company_name: data.company_name || 'SiteOrbit Technologies Ltd',
        company_address: data.company_address || 'Unit 5, Tech Park\n123 Innovation Drive\nLondon E1 6AN\nUnited Kingdom',
        terms_of_service_url: data.terms_of_service_url || '/terms-of-service',
        privacy_policy_url: data.privacy_policy_url || '/privacy-policy'
      });
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
      setErrorModal({
        show: true,
        message: 'Failed to load app metadata: ' + (error.response?.data?.detail || 'Unknown error')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features_enabled: {
        ...prev.features_enabled,
        [feature]: !prev.features_enabled[feature]
      }
    }));
    setHasChanges(true);
  };

  const saveMetadata = async () => {
    try {
      setSaving(true);
      
      // Detect changes and build changelog entry
      const changes = [];
      const fieldLabels = {
        release_notes: 'Release Notes',
        latest_app_version: 'Latest App Version',
        force_update: 'Force Update',
        update_url_ios: 'iOS Update URL',
        update_url_android: 'Android Update URL',
        maintenance_mode: 'Maintenance Mode',
        maintenance_message: 'Maintenance Message',
        support_email: 'Support Email',
        support_website: 'Support Website',
        company_name: 'Company Name',
        company_address: 'Company Address',
        terms_of_service_url: 'Terms of Service URL',
        privacy_policy_url: 'Privacy Policy URL'
      };
      
      // Compare old (metadata) vs new (formData)
      for (const [field, label] of Object.entries(fieldLabels)) {
        const oldValue = metadata?.[field];
        const newValue = formData[field];
        
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          const oldDisplay = oldValue === null || oldValue === '' ? 'empty' : String(oldValue);
          const newDisplay = newValue === null || newValue === '' ? 'empty' : String(newValue);
          changes.push(`${label}: ${oldDisplay} → ${newDisplay}`);
        }
      }
      
      // Check feature flag changes
      if (metadata?.features_enabled) {
        for (const [feature, enabled] of Object.entries(formData.features_enabled)) {
          const oldEnabled = metadata.features_enabled[feature];
          if (oldEnabled !== enabled) {
            changes.push(`Feature "${feature.replace(/_/g, ' ')}": ${oldEnabled ? 'enabled' : 'disabled'} → ${enabled ? 'enabled' : 'disabled'}`);
          }
        }
      }
      
      // Save metadata
      await axios.put(`${API}/super-admin/app/metadata`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // If there were changes, add automatic changelog entry
      if (changes.length > 0) {
        const now = new Date();
        const changelogEntry = {
          version: formData.latest_app_version,
          date: now.toISOString().split('T')[0], // YYYY-MM-DD
          changes: `Updated by Super Admin on ${now.toLocaleString()}:\n\n` + changes.map(c => `• ${c}`).join('\n')
        };
        
        try {
          await axios.post(`${API}/super-admin/app/metadata/changelog`, changelogEntry, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } catch (error) {
          console.error('Failed to add changelog entry:', error);
          // Don't fail the entire save if changelog fails
        }
      }
      
      await fetchMetadata();
      setHasChanges(false);
      setSuccessModal({
        show: true,
        message: changes.length > 0 
          ? `App metadata updated successfully! ${changes.length} change(s) logged to changelog.`
          : 'App metadata updated successfully!'
      });
    } catch (error) {
      console.error('Failed to save metadata:', error);
      setErrorModal({
        show: true,
        message: 'Failed to save metadata: ' + (error.response?.data?.detail || 'Unknown error')
      });
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    if (metadata) {
      setFormData({
        release_notes: metadata.release_notes || '',
        latest_app_version: metadata.latest_app_version || '',
        force_update: metadata.force_update || false,
        update_url_ios: metadata.update_url_ios || '',
        update_url_android: metadata.update_url_android || '',
        maintenance_mode: metadata.maintenance_mode || false,
        maintenance_message: metadata.maintenance_message || '',
        features_enabled: metadata.features_enabled || {},
        support_email: metadata.support_email || '',
        support_website: metadata.support_website || '',
        company_name: metadata.company_name || '',
        company_address: metadata.company_address || '',
        terms_of_service_url: metadata.terms_of_service_url || '',
        privacy_policy_url: metadata.privacy_policy_url || ''
      });
      setHasChanges(false);
    }
  };

  // Legal Document Functions
  const fetchLegalDocument = async (type) => {
    try {
      const endpoint = type === 'terms' ? '/legal/terms-of-service' : '/legal/privacy-policy';
      const response = await axios.get(`${API}${endpoint}`);
      if (response.data.success) {
        return {
          content: response.data.content,
          lastUpdated: response.data.last_updated
        };
      }
      return { content: '', lastUpdated: null };
    } catch (error) {
      console.error(`Failed to fetch ${type}:`, error);
      throw error;
    }
  };

  const openLegalEditor = async (type) => {
    try {
      const doc = await fetchLegalDocument(type);
      setLegalEditorModal({
        show: true,
        type,
        content: doc.content,
        saving: false
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: `Failed to load ${type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}: ${error.response?.data?.detail || 'Unknown error'}`
      });
    }
  };

  const openLegalPreview = async (type) => {
    try {
      const doc = await fetchLegalDocument(type);
      setLegalPreviewModal({
        show: true,
        type,
        content: doc.content,
        lastUpdated: doc.lastUpdated
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: `Failed to load ${type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}: ${error.response?.data?.detail || 'Unknown error'}`
      });
    }
  };

  // Legal document save confirmation modal state
  const [legalSaveConfirmModal, setLegalSaveConfirmModal] = useState({
    show: false,
    content: '',
    saving: false,
    notifying: false
  });

  const saveLegalDocument = async (content) => {
    // Show confirmation modal instead of saving directly
    setLegalSaveConfirmModal({
      show: true,
      content: content,
      saving: false,
      notifying: false
    });
  };

  const executeLegalDocumentSave = async (notifyUsers = false) => {
    try {
      setLegalSaveConfirmModal(prev => ({ 
        ...prev, 
        saving: true,
        notifying: notifyUsers 
      }));
      
      const endpoint = legalEditorModal.type === 'terms' 
        ? '/super-admin/legal/terms-of-service' 
        : '/super-admin/legal/privacy-policy';
      
      // Save the document
      await axios.put(`${API}${endpoint}`, { content: legalSaveConfirmModal.content }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // If notifyUsers is true, trigger the notification job
      if (notifyUsers) {
        const documentType = legalEditorModal.type === 'terms' ? 'terms_of_service' : 'privacy_policy';
        const documentUrl = legalEditorModal.type === 'terms' 
          ? `${window.location.origin}/terms-of-service`
          : `${window.location.origin}/privacy-policy`;
        
        try {
          await axios.post(`${API}/super-admin/legal/notify-users`, {
            document_type: documentType,
            document_url: documentUrl
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } catch (notifyError) {
          console.error('Failed to queue notification job:', notifyError);
          // Don't fail the whole operation if notification fails
        }
      }
      
      // Close both modals
      setLegalSaveConfirmModal({ show: false, content: '', saving: false, notifying: false });
      setLegalEditorModal({ show: false, type: null, content: '', saving: false });
      
      const documentName = legalEditorModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy';
      setSuccessModal({
        show: true,
        message: notifyUsers 
          ? `${documentName} updated successfully! Email notifications are being sent to all users.`
          : `${documentName} updated successfully!`
      });
      
    } catch (error) {
      setLegalSaveConfirmModal(prev => ({ ...prev, saving: false, notifying: false }));
      setErrorModal({
        show: true,
        message: `Failed to save: ${error.response?.data?.detail || 'Unknown error'}`
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl mb-8">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center mr-4">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">App Metadata</h1>
            <p className="text-gray-300">Manage mobile application version information and metadata</p>
          </div>
        </div>
      </div>

      {/* Fixed Action Panel at Bottom */}
      <div className="fixed bottom-0 left-80 right-0 bg-white border-t-2 border-gray-300 shadow-2xl z-40 px-8 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {hasChanges && (
              <div className="flex items-center text-amber-600 mr-4">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">You have unsaved changes</span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            {hasChanges && (
              <button
                onClick={resetChanges}
                className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors duration-200"
                disabled={saving}
              >
                Reset
              </button>
            )}
            
            <button
              onClick={saveMetadata}
              disabled={!hasChanges || saving}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                hasChanges
                  ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-2xl'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                'Save All Changes'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Section 1: Version Management */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
          Version Control
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Latest App Version
            </label>
            <input
              type="text"
              value={formData.latest_app_version}
              onChange={(e) => handleInputChange('latest_app_version', e.target.value)}
              placeholder="1.0.0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <p className="mt-1 text-xs text-gray-500">App compares device version to show update prompts</p>
          </div>

          <div className="flex items-end">
            <label className="flex items-center space-x-3 cursor-pointer p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 w-full">
              <input
                type="checkbox"
                checked={formData.force_update}
                onChange={(e) => handleInputChange('force_update', e.target.checked)}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-gray-700">Force Update</span>
                <p className="text-xs text-gray-500">Block app access until updated to this version</p>
              </div>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Notes
            </label>
            <textarea
              value={formData.release_notes}
              onChange={(e) => handleInputChange('release_notes', e.target.value)}
              rows="4"
              placeholder="Describe what's new in this version..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              iOS Update URL
            </label>
            <input
              type="url"
              value={formData.update_url_ios}
              onChange={(e) => handleInputChange('update_url_ios', e.target.value)}
              placeholder="https://apps.apple.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Android Update URL
            </label>
            <input
              type="url"
              value={formData.update_url_android}
              onChange={(e) => handleInputChange('update_url_android', e.target.value)}
              placeholder="https://play.google.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Section 2: Maintenance Mode */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-orange-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Maintenance Mode
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div>
              <label className="block text-sm font-medium text-gray-800">
                Enable Maintenance Mode
              </label>
              <p className="text-xs text-gray-600 mt-1">
                This will prevent ALL users from accessing the app
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!formData.maintenance_mode) {
                  setConfirmModal({
                    show: true,
                    message: 'This will prevent ALL users from accessing the app. Are you sure?',
                    onConfirm: () => handleInputChange('maintenance_mode', true)
                  });
                } else {
                  handleInputChange('maintenance_mode', false);
                }
              }}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                formData.maintenance_mode ? 'bg-orange-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.maintenance_mode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {formData.maintenance_mode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maintenance Message
              </label>
              <textarea
                value={formData.maintenance_message}
                onChange={(e) => handleInputChange('maintenance_message', e.target.value)}
                rows="3"
                placeholder="The app is currently under maintenance. Please try again later."
                maxLength={500}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <p className="mt-1 text-xs text-gray-500">{formData.maintenance_message?.length || 0}/500 characters</p>
            </div>
          )}
        </div>
      </div>

      {/* Section 3: Feature Flags */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
          </svg>
          Feature Flags
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(formData.features_enabled).map((feature) => (
            <div key={feature} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-800 capitalize">
                {feature.replace(/_/g, ' ')}
              </span>
              <button
                type="button"
                onClick={() => handleFeatureToggle(feature)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                  formData.features_enabled[feature] ? 'bg-green-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.features_enabled[feature] ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4: Support Information */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          Support Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
            <input
              type="email"
              value={formData.support_email}
              onChange={(e) => handleInputChange('support_email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Support Website</label>
            <input
              type="url"
              value={formData.support_website}
              onChange={(e) => handleInputChange('support_website', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Section 5: Company Information */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd" />
          </svg>
          Company Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Registered Address</label>
            <textarea
              value={formData.company_address}
              onChange={(e) => handleInputChange('company_address', e.target.value)}
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Terms of Service URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.terms_of_service_url}
                onChange={(e) => handleInputChange('terms_of_service_url', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                onClick={() => openLegalEditor('terms')}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                title="Edit Terms of Service content"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button
                onClick={() => openLegalPreview('terms')}
                className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                title="Preview Terms of Service"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Policy URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.privacy_policy_url}
                onChange={(e) => handleInputChange('privacy_policy_url', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <button
                onClick={() => openLegalEditor('privacy')}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                title="Edit Privacy Policy content"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button
                onClick={() => openLegalPreview('privacy')}
                className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                title="Preview Privacy Policy"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview & Testing */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-pink-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
          </svg>
          Preview & Testing
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Mobile Preview Mockup */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Mobile App Preview</h3>
            <div className="bg-gray-100 rounded-2xl p-4 border-4 border-gray-800 shadow-xl max-w-sm mx-auto">
              <div className="bg-white rounded-xl p-4 space-y-3">
                <div className="text-center border-b pb-3">
                  <h4 className="text-lg font-bold text-gray-800">Settings</h4>
                </div>
                
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Latest Version</span>
                    <span className="font-semibold">{formData.latest_app_version}</span>
                  </div>
                  
                  {formData.force_update && (
                    <div className="bg-red-50 border border-red-200 rounded p-2 my-2">
                      <p className="text-red-800 font-semibold">Update Required!</p>
                      <p className="text-red-600 text-xs">You must update to continue using the app</p>
                    </div>
                  )}
                  
                  {formData.maintenance_mode && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-2 my-2">
                      <p className="text-orange-800 font-semibold">Maintenance Mode</p>
                      <p className="text-orange-600 text-xs">{formData.maintenance_message}</p>
                    </div>
                  )}
                  
                  <div className="pt-2">
                    <p className="text-gray-600 font-semibold mb-1">Support</p>
                    <p className="text-gray-800">{formData.support_email}</p>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-gray-600 font-semibold mb-1">Legal</p>
                    <a href={formData.terms_of_service_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block">Terms of Service</a>
                    <a href={formData.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline block">Privacy Policy</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* JSON Preview & Testing */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">API Response Preview</h3>
            <div className="bg-gray-900 rounded-lg p-4 text-xs text-green-400 font-mono overflow-auto max-h-96">
              <pre>{JSON.stringify(formData, null, 2)}</pre>
            </div>
            
            <button
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/app/metadata`);
                  setSuccessModal({
                    show: true,
                    message: 'API Test Successful! Check console for response.'
                  });
                  console.log('API Response:', response.data);
                } catch (error) {
                  setErrorModal({
                    show: true,
                    message: 'API Test Failed: ' + error.message
                  });
                }
              }}
              className="mt-3 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              Test Public API Endpoint
            </button>
          </div>
        </div>
      </div>

      {/* Changelog Management */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <svg className="w-6 h-6 text-teal-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
          Change History
        </h2>

        {metadata?.changelog && metadata.changelog.length > 0 ? (
          <div className="space-y-3">
            {metadata.changelog.map((entry, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
                      v{entry.version}
                    </span>
                    <span className="text-sm text-gray-600">{entry.date}</span>
                  </div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line">
                  {entry.changes}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600">No change history yet</p>
            <p className="text-sm text-gray-500 mt-1">Changes will be automatically logged when you save metadata updates</p>
          </div>
        )}
        
        <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <p className="text-sm text-teal-800">
            <span className="font-semibold">📝 Auto-Generated:</span> Every time you save changes to app metadata, 
            a new changelog entry is automatically created showing what changed, when, and by whom.
          </p>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />

      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
        variant="danger"
      />

      {/* Legal Document Editor Modal */}
      <LegalDocumentEditorModal
        isOpen={legalEditorModal.show}
        onClose={() => setLegalEditorModal({ show: false, type: null, content: '', saving: false })}
        onSave={saveLegalDocument}
        title={legalEditorModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        content={legalEditorModal.content}
        saving={legalEditorModal.saving}
      />

      {/* Legal Document Preview Modal */}
      <LegalDocumentPreviewModal
        isOpen={legalPreviewModal.show}
        onClose={() => setLegalPreviewModal({ show: false, type: null, content: '', lastUpdated: null })}
        title={legalPreviewModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
        content={legalPreviewModal.content}
        lastUpdated={legalPreviewModal.lastUpdated}
      />

      {/* Legal Document Save Confirmation Modal */}
      {legalSaveConfirmModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="legal-save-confirm-modal">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !legalSaveConfirmModal.saving && setLegalSaveConfirmModal({ show: false, content: '', saving: false, notifying: false })}
            ></div>

            {/* Modal */}
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Save {legalEditorModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You are about to update the {legalEditorModal.type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}. 
                      Would you like to notify all users about this change?
                    </p>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex">
                        <svg className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-amber-700">
                          <strong>Note:</strong> Selecting &quot;Save &amp; Notify&quot; will send an email to ALL active users platform-wide informing them of the updated document.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={() => executeLegalDocumentSave(true)}
                  disabled={legalSaveConfirmModal.saving}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-and-notify-btn"
                >
                  {legalSaveConfirmModal.saving && legalSaveConfirmModal.notifying ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Save & Notify Users
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => executeLegalDocumentSave(false)}
                  disabled={legalSaveConfirmModal.saving}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-only-btn"
                >
                  {legalSaveConfirmModal.saving && !legalSaveConfirmModal.notifying ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Only'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setLegalSaveConfirmModal({ show: false, content: '', saving: false, notifying: false })}
                  disabled={legalSaveConfirmModal.saving}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="cancel-save-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppMetadataPage;
